// src/pages/offlineSales/components/CustomerSelectorModal.jsx
//
// FK-based customer selection for Offline Sales — conceptually mirrors
// src/pages/pos/terminal/components/CustomerSelectorModal.jsx, adapted for the
// Offline Sales customers API (which supports credit-sale fields: creditLimit,
// creditTermDays) instead of the POS module's customer set.
import React, { useState, useEffect } from 'react';
import { X, Search, Plus } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { getCustomers } from '../../../services/api/offlineSales/customers';
import CustomerModal from './CustomerModal';
import styles from './CustomerSelectorModal.module.css';

const CustomerSelectorModal = ({ current, onSelect, onClose }) => {
  const { t } = useLocalization();
  const { success } = useAlert();
  const [query, setQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 300);
    return () => clearTimeout(timer);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { search(''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const search = async (q) => {
    try {
      setLoading(true);
      const result = await getCustomers({ search: q, limit: 20 });
      setCustomers(result.customers || []);
    } catch {
      // silent — empty list is an acceptable degraded state
    } finally {
      setLoading(false);
    }
  };

  if (showCreate) {
    return (
      <CustomerModal
        customer={null}
        onClose={() => setShowCreate(false)}
        onSaved={(savedCustomer) => {
          setShowCreate(false);
          success(t('offlineSales.customerCreatedSuccess'));
          onSelect(savedCustomer);
        }}
      />
    );
  }

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{t('offlineSales.selectCustomer')}</h3>
          <button onClick={onClose} className={styles.modalClose}><X size={18} /></button>
        </div>

        <div className={styles.searchRow}>
              <Search size={14} />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('offlineSales.searchCustomers')}
                className={styles.modalSearch}
              />
        </div>

        <div className={styles.customerList}>
          <button
            className={`${styles.customerRow} ${!current ? styles.selected : ''}`}
            onClick={() => onSelect(null)}
          >
            <span className={styles.customerName}>{t('offlineSales.cashSales')}</span>
          </button>

          {loading ? (
            <div className={styles.listLoading}>...</div>
          ) : customers.length === 0 ? (
            <div className={styles.listEmpty}>{t('offlineSales.noCustomersFound')}</div>
          ) : (
            customers.map(c => (
              <button
                key={c.id}
                className={`${styles.customerRow} ${current?.id === c.id ? styles.selected : ''}`}
                onClick={() => onSelect(c)}
              >
                <span className={styles.customerName}>{c.name}</span>
                <span className={styles.customerPhone}>{c.phone || ''}</span>
                {c.creditLimit != null && (
                  <span className={styles.customerCreditBadge}>
                    {t('offlineSales.creditLimit')}: {c.creditLimit}
                  </span>
                )}
              </button>
            ))
          )}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.actionBtnSecondary} onClick={() => setShowCreate(true)}>
            <Plus size={14} /> {t('offlineSales.newCustomer')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerSelectorModal;
